import { Suspense, useEffect, useState } from 'react';
import { ChannelModal } from '@/components/ChannelModal';
import { DmFoldersModal } from '@/components/DmFoldersModal';
import { GlobalSearchModal } from '@/components/GlobalSearchModal';
import { UserSearchModal } from '@/components/UserSearchModal';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { onAppEvent, type ChannelSettingsSpec } from '@/lib/appEvents';
import { lazyWithReload } from '@/lib/lazyWithReload';
import type { GuildSettingsTab } from '@/hooks/useGuildSettings';

const ServerSettingsModal = lazyWithReload(() => import('@/components/ServerSettingsModal').then((m) => ({ default: m.ServerSettingsModal })));
const guildTabs: GuildSettingsTab[] = ['overview', 'members', 'roles', 'channelOverrides', 'invites', 'bans', 'auditLog', 'reports', 'emojis'];

/** Form sessions outlive responsive navigation, with one global event consumer. */
export function WorkspacePanelsHost() {
  const [guildSession, setGuildSession] = useState<{ id: string; tab?: GuildSettingsTab } | null>(null);
  const [channelSession, setChannelSession] = useState<ChannelSettingsSpec | null>(null);
  const [utility, setUtility] = useState<'folders' | 'search' | 'people' | null>(null);
  const guild = useDataStore((s) => guildSession ? s.guilds[guildSession.id] : undefined);
  const channelGuildId = channelSession?.kind === 'edit' ? channelSession.channel.guildId : channelSession?.guildId;
  const channelGuild = useDataStore((s) => channelGuildId ? s.guilds[channelGuildId] : undefined);
  const myId = useAuthStore((s) => s.user?.id);
  const channelExists = channelSession?.kind === 'edit'
    ? channelGuild?.channels.some(({ id }) => id === channelSession.channel.id)
    : Boolean(channelGuild);
  useEffect(() => onAppEvent('guild:settings', (detail) => {
    const id = useDataStore.getState().currentGuildId;
    if (id) setGuildSession((current) => current ?? { id, tab: guildTabs.find((tab) => tab === detail?.tab) });
  }), []);
  useEffect(() => onAppEvent('channel:settings', (spec) => setChannelSession((current) => current ?? spec)), []);
  useEffect(() => onAppEvent('dm-folders:manage', () => setUtility('folders')), []);
  useEffect(() => onAppEvent('search:open', () => setUtility('search')), []);
  useEffect(() => onAppEvent('people:open', () => setUtility('people')), []);
  useEffect(() => { if (guildSession && !guild) setGuildSession(null); }, [guildSession, guild]);
  useEffect(() => { if (channelSession && !channelExists) setChannelSession(null); }, [channelSession, channelExists]);
  return <>
    {guildSession && guild && <Suspense fallback={null}>
      <ServerSettingsModal guild={guild} isOwner={guild.ownerId === myId} memberCount={0}
        initialTab={guildSession.tab} onClose={() => setGuildSession(null)} />
    </Suspense>}
    <ChannelModal spec={channelExists ? channelSession : null} onClose={() => setChannelSession(null)} />
    <DmFoldersModal open={utility === 'folders'} onClose={() => setUtility(null)} />
    <GlobalSearchModal open={utility === 'search'} onClose={() => setUtility(null)} />
    <UserSearchModal open={utility === 'people'} onClose={() => setUtility(null)} />
  </>;
}
